<template>
  <div class="joingame-page">
    <div class="joingame-container">
      <button class="back-btn" @click="$router.back()">← Back</button>
      <h1 class="page-title">⚔️ Join a Game as Admin</h1>
      <p class="page-subtitle">
        Select a game below and send a petition to its current admins to join as a co-admin.
      </p>

      <!-- Loading -->
      <div v-if="loading" class="status-msg">Loading games…</div>

      <!-- Error -->
      <div v-else-if="loadError" class="status-msg status-msg--error">
        Could not load games. Please try again later.
      </div>

      <!-- Game dropdown -->
      <div v-else class="game-select-wrap">
        <select class="game-select" v-model="selectedGame" @change="resultMsg = null">
          <option :value="null" disabled>Select a game…</option>
          <option v-for="game in games" :key="game.IdGame" :value="game">{{ game.GameName }}</option>
        </select>
      </div>

      <!-- Email consent -->
      <label v-if="!loading && !loadError" class="consent-label">
        <input type="checkbox" v-model="shareEmail" class="consent-checkbox" />
        <span>
          I consent to sharing my account email
          <strong>({{ userEmail }})</strong>
          with the game admins so they can contact me.
        </span>
      </label>

      <!-- Action -->
      <div class="action-row">
        <button
          class="petition-btn"
          :disabled="!selectedGame || sending"
          @click="sendPetition"
        >
          <span v-if="sending">Sending…</span>
          <span v-else>📨 Send Game Admin Petition</span>
        </button>
      </div>

      <!-- Result feedback -->
      <div v-if="resultMsg" class="result-msg" :class="resultOk ? 'result-msg--ok' : 'result-msg--err'">
        {{ resultMsg }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'JoinGame',

  data() {
    return {
      loading: true,
      loadError: false,
      games: [],
      selectedGame: null,
      shareEmail: false,
      sending: false,
      resultMsg: null,
      resultOk: false,
    };
  },

  computed: {
    userEmail() {
      return this.$store.state.email || '';
    },
  },

  mounted() {
    this.fetchGames();
  },

  methods: {    async fetchGames() {
      try {
        const res = await fetch('/GetGamesWeb/', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'include',
        });
        const data = await res.json();
        if (data && data.success) {
          this.games = data.games;
        } else {
          this.loadError = true;
        }
      } catch {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },

    async sendPetition() {
      if (!this.selectedGame || this.sending) return;
      this.sending = true;
      this.resultMsg = null;
      try {
        const res = await fetch('/RequestGameAdmin', {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            gameId: this.selectedGame.IdGame,
            shareEmail: this.shareEmail,
          }),
        });
        const data = await res.json();
        if (data && data.success) {
          this.resultOk = true;
          this.resultMsg = `Your petition for "${this.selectedGame.GameName}" has been sent to its admins!`;
          this.selectedGame = null;
        } else {
          this.resultOk = false;
          this.resultMsg = data && data.error ? data.error : 'Something went wrong. Please try again.';
        }
      } catch {
        this.resultOk = false;
        this.resultMsg = 'Network error. Please try again.';
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.joingame-page {
  min-height: 100vh;
  background: #0a121e;
  color: #e0e0e0;
  font-family: Arial, sans-serif;
  display: flex;
  justify-content: center;
  padding: 40px 16px 60px;
}

.joingame-container {
  width: 100%;
  max-width: 680px;
}

.back-btn {
  background: none;
  border: none;
  color: #5dade2;
  font-size: 0.95rem;
  cursor: pointer;
  padding: 0 0 20px;
  display: block;
}
.back-btn:hover { color: #aad4f5; }

.page-title {
  font-size: 2rem;
  margin: 0 0 8px;
}

.page-subtitle {
  color: #8a9bb0;
  margin: 0 0 28px;
  font-size: 0.95rem;
  line-height: 1.5;
}

.status-msg {
  padding: 16px;
  border-radius: 8px;
  background: #111d2e;
  color: #8a9bb0;
  margin-bottom: 20px;
}
.status-msg--error { color: #e74c3c; }

/* Game dropdown */
.game-select-wrap {
  margin-bottom: 24px;
}

.game-select {
  width: 100%;
  background: #111d2e;
  color: #e0e0e0;
  border: 2px solid #2471a3;
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 1rem;
  cursor: pointer;
  appearance: auto;
}
.game-select:focus {
  outline: none;
  border-color: #5dade2;
}

/* Consent */
.consent-label {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.9rem;
  color: #b0c4d8;
  line-height: 1.5;
  margin-bottom: 24px;
  cursor: pointer;
}
.consent-checkbox {
  margin-top: 3px;
  accent-color: #2471a3;
  flex-shrink: 0;
}

/* Action */
.action-row {
  display: flex;
  justify-content: flex-end;
}

.petition-btn {
  background: #2471a3;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 28px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, opacity 0.2s;
}
.petition-btn:hover:not(:disabled) { background: #1a5580; }
.petition-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Result */
.result-msg {
  margin-top: 20px;
  padding: 14px 18px;
  border-radius: 8px;
  font-size: 0.93rem;
  line-height: 1.5;
}
.result-msg--ok {
  background: #0d2e1a;
  color: #7ef7a6;
  border: 1px solid #1a6e3a;
}
.result-msg--err {
  background: #2e0d0d;
  color: #f7a6a6;
  border: 1px solid #7e1a1a;
}
</style>
